import { LIMITS_PATH } from '@/features/limits/limits-tabs';

import { anchor } from './setting-anchors.types';

import type { SettingAnchor } from './setting-anchors.types';

/**
 * The `/limits` anchors — the consolidated budget / alerts / traffic hub
 * (ADR-0543).
 *
 * Its own file for the same reason the workspace-settings block has one: the
 * registry crossed the ~300-line ceiling, and this is a whole page's worth of
 * anchors rather than an arbitrary slice. Spread back into the one
 * `SETTING_ANCHORS` map next door, with the coverage convention test failing on
 * anything forgotten.
 */
export const LIMITS_SETTING_ANCHORS = {
  // `/limits` (ADR-0543) — the consolidated hub. Its regions are NOT
  // `SectionCard`s: the tab body already draws the outline, so each is a heading
  // plus a bordered list (`ui-design.md` §0, box-in-box). They land through
  // `settingAnchorProps` instead.
  //
  // Each anchor claims the permission that decides whether its region RENDERS,
  // not the one that decides whether its controls are live — a viewer who can
  // see a ceiling should be able to find it, and lands on a card whose inputs
  // are disabled, which is honest.
  'spend-cap': anchor(
    'setting-spend-cap',
    LIMITS_PATH,
    'limits.budget.headlineLabel',
    'billing',
    'view',
    {
      keywordsKey: 'settingSearch.spendCap',
      pane: 'budget',
      controls: [
        {
          id: 'narrower',
          labelKey: 'limits.budget.narrower',
          keywordsKey: 'settingSearch.spendCap.narrower',
        },
        {
          id: 'thresholds',
          labelKey: 'limits.budget.thresholdsLabel',
          keywordsKey: 'settingSearch.spendCap.thresholds',
          // The warning ladder is edited only in Advanced (§20). Offering the
          // field in Simple would land on nothing, and offering it WITH a mode
          // switch would be the search changing a preference about the person
          // to satisfy a query.
          requiresAdvanced: true,
        },
      ],
    },
  ),
  'rate-limit': anchor(
    'setting-rate-limit',
    LIMITS_PATH,
    'limits.traffic.headlineLabel',
    'settings',
    'view',
    {
      keywordsKey: 'settingSearch.rateLimit',
      pane: 'traffic',
      controls: [
        {
          id: 'quotas',
          labelKey: 'limits.advanced.quotas',
          keywordsKey: 'settingSearch.rateLimit.quotas',
          requiresAdvanced: true,
        },
      ],
    },
  ),
  // The three alarm families that carry controls. The fourth region
  // ("Everything else that can reach you") is deliberately absent: it explains
  // what already reaches an operator and has nothing to set, so landing on it
  // would answer a search for an option with a paragraph.
  'usage-alerts': anchor(
    'setting-usage-alerts',
    LIMITS_PATH,
    'alerts.usage.title',
    'billing',
    'view',
    {
      keywordsKey: 'settingSearch.usageAlerts',
      pane: 'alerts',
    },
  ),
  'metric-alerts': anchor(
    'setting-metric-alerts',
    LIMITS_PATH,
    'alerts.metrics.title',
    'analytics',
    'view',
    { keywordsKey: 'settingSearch.metricAlerts', pane: 'alerts' },
  ),
  'audience-alerts': anchor(
    'setting-audience-alerts',
    LIMITS_PATH,
    'alerts.audiences.title',
    'audiences',
    'view',
    { keywordsKey: 'settingSearch.audienceAlerts', pane: 'alerts' },
  ),
} as const satisfies Record<string, SettingAnchor>;
